import React, { useState, useEffect } from 'react';

const calcProfit = (betSize, betsPerDay, ev, subscription) => {
  const days = 30;
  const expectedReturn = betSize * betsPerDay * days * (ev / 100);
  const profit = expectedReturn - subscription;
  return { expectedReturn, profit };
};

export const ProfitCalculator = () => {
  // For nonlinear slider mapping
  const sliderMin = 0;
  const sliderMax = 100;

  // Map slider value to bet size
  const sliderToBetSize = (sliderValue) => {
    if (sliderValue <= 80) {
      // 0-80 maps to 1-100
      return Math.round(1 + (sliderValue / 80) * 99);
    } else {
      // 81-100 maps to 100-1000
      return Math.round(100 + ((sliderValue - 80) / 20) * 900);
    }
  };

  // Map bet size to slider value
  const betSizeToSlider = (betSize) => {
    if (betSize <= 100) {
      return ((betSize - 1) / 99) * 80;
    } else {
      return 80 + ((betSize - 100) / 900) * 20;
    }
  };

  const [sliderValue, setSliderValue] = useState(betSizeToSlider(100));
  const [betSize, setBetSize] = useState(100);
  const [betsPerDay, setBetsPerDay] = useState(10);
  const [ev, setEv] = useState('5');
  const [result, setResult] = useState(null);
  const subscription = 63.98; // Monthly (converted from biweekly $31.99)

  // Responsive: detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 750);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 750);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSliderChange = (e) => {
    const val = Number(e.target.value);
    setSliderValue(val);
    setBetSize(sliderToBetSize(val));
  };

  // Format number with commas
  const formatNumber = (num) => {
    return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Automatically calculate profit whenever inputs change
  useEffect(() => {
    if (!betSize || !betsPerDay || !ev) return;
    const { expectedReturn, profit } = calcProfit(
      Number(betSize),
      Number(betsPerDay),
      Number(ev),
      subscription
    );
    setResult({
      expectedReturn: expectedReturn.toFixed(2),
      profit: profit.toFixed(2),
    });
  }, [betSize, betsPerDay, ev]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      background: '#18181A',
      borderRadius: 16,
      padding: isMobile ? 8 : 20,
      color: 'white',
      maxWidth: isMobile ? 340 : 1200,
      margin: isMobile ? '8px auto' : '0 auto',
      boxShadow: '0 2px 16px #0003',
      width: isMobile ? '98vw' : 'auto',
      minHeight: isMobile ? 0 : undefined,
    }}>
      {/* Left: Inputs */}
      <div style={{ flex: 1, paddingRight: isMobile ? 0 : 24, marginBottom: isMobile ? 12 : 0 }}>
        <h2 style={{ fontWeight: 600, fontSize: isMobile ? 15 : 20, marginBottom: isMobile ? 6 : 4 }}>Profit Calculator</h2>
        <div style={{ fontSize: isMobile ? 10 : 12, color: '#bdbdbd', marginBottom: isMobile ? 10 : 12 }}>
          See whether our betting tools will be profitable for your betting strategy.
        </div>
        <div style={{ marginBottom: isMobile ? 10 : 14 }}>
          <div style={{ marginBottom: 4, fontSize: isMobile ? 11 : 13 }}>Average Bet Size</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              value={sliderValue}
              onChange={handleSliderChange}
              style={{ flex: 1 }}
            />
            <div style={{
              background: '#232326',
              border: '1px solid #333',
              borderRadius: 6,
              padding: '6px 12px',
              color: '#7fff7f',
              fontSize: isMobile ? 13 : 15,
              fontWeight: 500,
              minWidth: 80,
              textAlign: 'center',
            }}>
              ${betSize.toLocaleString()}
            </div>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', 
          gap: isMobile ? 0 : 12,
          marginBottom: isMobile ? 10 : 14 
        }}>
          <div style={{ flex: 1, marginBottom: isMobile ? 10 : 0 }}>
            <div style={{ marginBottom: 4, fontSize: isMobile ? 11 : 13 }}>Bets Per Day</div>
            <input
              type="number"
              min={1}
              value={betsPerDay}
              onChange={e => setBetsPerDay(e.target.value)}
              style={{ width: '100%', padding: isMobile ? 5 : 8, borderRadius: 4, border: '1px solid #333', background: '#232326', color: 'white', fontSize: isMobile ? 12 : 14 }}
              placeholder="-"
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4, fontSize: isMobile ? 11 : 13 }}>Expected Value (%)</div>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={ev}
              onChange={e => setEv(e.target.value)}
              style={{ width: '100%', padding: isMobile ? 5 : 8, borderRadius: 4, border: '1px solid #333', background: '#232326', color: 'white', fontSize: isMobile ? 12 : 14 }}
              placeholder="-"
            />
          </div>
        </div>
      </div>
      {/* Right: Results */}
      <div style={{
        flex: 1,
        background: '#1e1e21',
        borderRadius: 12,
        padding: isMobile ? 8 : 20,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minWidth: isMobile ? 'unset' : 0,
      }}>
        <div style={{
          background: result && result.profit < 0 
            ? 'linear-gradient(135deg, rgba(255, 127, 127, 0.1) 0%, rgba(255, 127, 127, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(127, 255, 127, 0.1) 0%, rgba(127, 255, 127, 0.05) 100%)',
          border: result && result.profit < 0 
            ? '1px solid rgba(255, 127, 127, 0.2)'
            : '1px solid rgba(127, 255, 127, 0.2)',
          borderRadius: 12,
          padding: isMobile ? 16 : 24,
          marginBottom: isMobile ? 12 : 16,
          textAlign: 'center',
        }}>
          <div style={{
            color: '#bdbdbd',
            fontSize: isMobile ? 9 : 11,
            marginBottom: isMobile ? 6 : 8,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Expected Monthly Profit
          </div>
          <div style={{
            color: result && result.profit < 0 ? '#ff7f7f' : '#7fff7f',
            fontSize: isMobile ? 24 : 32,
            fontWeight: 700,
            lineHeight: 1.2,
          }}>
            ${result ? formatNumber(result.profit) : '0.00'}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: isMobile ? 6 : 6, paddingBottom: isMobile ? 6 : 8, borderBottom: '1px solid #2a2a2e' }}>
          <div style={{ color: '#bdbdbd', fontSize: isMobile ? 9 : 11 }}>Expected Return</div>
          <div style={{ color: 'white', fontWeight: 500, fontSize: isMobile ? 10 : 13 }}>${result ? formatNumber(result.expectedReturn) : '0.00'}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: isMobile ? 6 : 8, paddingBottom: isMobile ? 6 : 8, borderBottom: '1px solid #2a2a2e' }}>
          <div style={{ color: '#bdbdbd', fontSize: isMobile ? 9 : 11 }}>Subscription</div>
          <div style={{ color: 'white', fontWeight: 500, fontSize: isMobile ? 10 : 13 }}>-${subscription}</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: isMobile ? 6 : 12 }}>
          <div style={{ color: 'white', fontWeight: 600, fontSize: isMobile ? 10 : 13 }}>Net Profit</div>
          <div style={{ color: result && result.profit < 0 ? '#ff7f7f' : '#7fff7f', fontWeight: 700, fontSize: isMobile ? 11 : 15 }}>${result ? formatNumber(result.profit) : '0.00'}</div>
        </div>
        <div style={{
          background: 'transparent',
          border: 'none',
          borderRadius: 8,
          padding: isMobile ? 8 : 0,
          marginTop: isMobile ? 6 : 0,
          color: result && result.profit > 0 ? '#7fff7f' : '#bdbdbd',
          width: '100%',
          fontSize: isMobile ? 9 : 11,
          lineHeight: 1.4,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          {result ? (
            result.profit > 0 ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#7fff7f"/>
                </svg>
                <span>You're likely profitable at these settings.</span>
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#ff7f7f"/>
                </svg>
                <span>You may not be profitable at these settings.</span>
              </>
            )
          ) : (
            <span>Enter your values to see your expected profit.</span>
          )}
        </div>
        <div style={{ color: '#bdbdbd', fontSize: isMobile ? 7 : 8, marginTop: isMobile ? 8 : 12, textAlign: 'left', lineHeight: 1.4 }}>
          This calculator provides an estimation based on your inputs. Actual results may vary based on market conditions and betting.
        </div>
      </div>
    </div>
  );
};

export default ProfitCalculator; 